import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  completionTimeQuery,
  mailCompletionReport,
  performancePageQuery,
  type CompletionStep,
  type CompletionTicket,
} from '../api/easeedeskPerformance'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { durationString } from './format'

/**
 * "My Performance" - `Easeedesk::my_performance`, macheta
 * `views/version-3/easeedesk/my_performance/index.php`.
 *
 * ADRESA. Ecranul asta sta la `/app/easeedesk/my_performance/completion_time`,
 * nu la adresa scurta. In portal, `easeedesk/my_performance` NU ajunge la
 * `Easeedesk::my_performance`, ci la `easeedesk-dashboards/My_performance` -
 * TABLOUL, cel din meniu (vezi `MyPerformanceDashboardPage`). Metoda din
 * `Easeedesk.php` a ramas fara adresa; raportul pe care il deschidea coloana
 * ei, in schimb, are ruta lui vie - `easeedesk/my_performance/completion_time` -
 * si aia e adresa pe care sta acum coloana cu tot cu raport.
 *
 * Aceeasi asezare: coloana verde din stanga cu doua intrari si panoul care le
 * incarca. Aceleasi zece coloane in tabelul de Completion Time, in aceeasi
 * ordine, si aceleasi doua randuri pe tichet - primul cu numele starilor, al
 * doilea cu cat s-a stat in ele si cu eticheta EXCEEDED / EARLIER.
 *
 * DOUA LUCRURI RAMAN CUM ERAU, DINADINS:
 *
 *  - "My Tickets" scrie "Something went wrong. Please try again later.".
 *    Asta scrie si in portal: `easeedesk-reports/My_performance::my_tickets`
 *    nu face nimic altceva. Nu s-a inventat un raport care nu exista;
 *  - butonul "Export" nu face nimic. Nici in portal nu avea niciun ascultator.
 *    O mutare nu e locul unde se adauga functii, dar nici unde se sterg pe
 *    tacute: ramane, si scrie aici de ce.
 *
 * Foaia e cea din macheta, cuvant cu cuvant, prinsa sub `.od-my-performance`:
 * in portal blocul `<style>` traia cat pagina, aici pagina nu se mai reincarca.
 */

const STYLE = `
.od-my-performance #my-performance-sidebar li a { color: #298f75; }
.od-my-performance #my-performance-sidebar li.active a { color: #23242A; }
`

export function MyPerformancePage() {
  const page = useQuery(performancePageQuery)
  const [open, setOpen] = useState('completion_time')

  const pages = page.data?.pages ?? []

  return (
    <Content>
      <style>{STYLE}</style>
      <div className="od-my-performance" id="my_performance">
        <aside
          style={{
            position: 'fixed',
            margin: '-20px',
            backgroundColor: '#f5f5f5',
            width: '270px',
            height: '100%',
            borderRight: '1px solid #ddd',
            top: '70px',
            zIndex: 1,
            paddingTop: '49px',
          }}
        >
          <ul id="my-performance-sidebar" style={{ padding: '20px 0' }}>
            {pages.map((one) => (
              <li key={one.key} className={one.key === open ? 'active' : ''} style={{ padding: '2px 30px', fontSize: '12px' }}>
                <a
                  href={`/app/easeedesk/my_performance/completion_time?page=${one.key}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setOpen(one.key)
                  }}
                >
                  {one.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <div id="my-performance-content" style={{ marginLeft: '270px' }}>
          {open === 'completion_time' ? <CompletionTimePanel /> : <MyTicketsPanel />}
        </div>
      </div>
    </Content>
  )
}

/** Exact randul pe care il scrie `My_performance::my_tickets` in portal. */
function MyTicketsPanel() {
  return (
    <div className="alert alert-warning">
      <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Something went wrong. Please try again later.
    </div>
  )
}

function today(): string {
  const now = new Date()
  const two = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())}`
}

function CompletionTimePanel() {
  /* Casuta "Created" porneste pe ziua de azi, ca in macheta; "Resolved" goala. */
  const [createdBox, setCreatedBox] = useState(today())
  const [resolvedBox, setResolvedBox] = useState('')
  const [created, setCreated] = useState(today())
  const [resolved, setResolved] = useState('')
  const [filters, setFilters] = useState(false)
  const [mail, setMail] = useState(false)
  const [email, setEmail] = useState('')

  const report = useQuery(completionTimeQuery(created, resolved))
  const max = report.data?.maxStatus ?? 0
  const tickets = report.data?.tickets ?? []

  async function onSend() {
    try {
      const out = await mailCompletionReport(email, created, resolved)
      if (!out.ok) {
        toast(out.message ?? 'Not sent', 'Error', 'error')
        return
      }
      setMail(false)
      setEmail('')
      toast('Report sent.', 'Success', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Not sent', 'Error', 'error')
    }
  }

  return (
    <>
      <h5>Completion time</h5>
      <hr />

      <div className="row" style={{ marginBottom: '10px' }}>
        <div className="col-md-6">
          <button type="button" className="btn btn-sm btn-default" onClick={() => setFilters(!filters)} aria-expanded={filters}>
            <i className="fa fa-filter" aria-hidden="true"></i> Filter
          </button>
        </div>
        <div className="col-md-6 align-right">
          <button type="button" className="btn btn-sm" onClick={() => setMail(true)}>
            <i className="far fa-envelope" aria-hidden="true"></i>
          </button>
          {/* Fara ascultator, ca in portal - vezi comentariul din capul paginii. */}
          <button type="button" className="btn btn-sm">
            <i className="fa fa-download" aria-hidden="true"></i> Export
          </button>
        </div>
      </div>

      {filters && (
        <div>
          <div className="well" style={{ backgroundColor: '#f5f5f5' }}>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="created_filter">Created</label>
                  <div className="input-group">
                    <input
                      type="date"
                      id="created_filter"
                      className="form-control input-sm"
                      value={createdBox}
                      onChange={(e) => setCreatedBox(e.target.value)}
                    />
                    <span className="input-group-btn">
                      <button className="btn btn-danger btn-sm" type="button" onClick={() => setCreatedBox('')}>
                        <i className="fas fa-times"></i>
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="resolved_filter">Resolved</label>
                  <div className="input-group">
                    <input
                      type="date"
                      id="resolved_filter"
                      className="form-control input-sm"
                      value={resolvedBox}
                      onChange={(e) => setResolvedBox(e.target.value)}
                    />
                    <span className="input-group-btn">
                      <button className="btn btn-danger btn-sm" type="button" onClick={() => setResolvedBox('')}>
                        <i className="fas fa-times"></i>
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <button
                type="button"
                className="btn btn-sm btn-primary submitFilters"
                onClick={() => {
                  setCreated(createdBox)
                  setResolved(resolvedBox)
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <table id="completion_time_table" className="table table-hover table-bordered">
        <thead>
          <tr style={{ backgroundColor: '#fff' }}>
            <th className="align-left">ID</th>
            <th className="align-left">Company</th>
            <th className="align-left">Subject</th>
            <th className="align-left">Category</th>
            <th className="align-left">Subcategory</th>
            <th className="align-left">Operation</th>
            <th className="align-left">Estimated Effort</th>
            <th className="align-left">Real Effort</th>
            <th className="align-left">Total Effort Time</th>
            <th className="align-left statusCompletionCol" colSpan={max > 0 ? max : 2}>
              Status Completion
            </th>
          </tr>
        </thead>
        <tbody>
          {report.isPending ? (
            <tr>
              <td className="center" colSpan={10}>
                Loading data from server...
              </td>
            </tr>
          ) : tickets.length === 0 ? (
            <tr>
              <td className="text-center" colSpan={10}>
                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> No results
              </td>
            </tr>
          ) : (
            tickets.map((ticket) => <TicketRows ticket={ticket} max={max} key={ticket.ID} />)
          )}
        </tbody>
      </table>

      {mail && (
        <div className="modal fade in" style={{ display: 'block' }} role="dialog" aria-labelledby="emailModalLabel">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button type="button" className="close" aria-label="Close" onClick={() => setMail(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
                <h4 className="modal-title" id="emailModalLabel">
                  Send report by email
                </h4>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="recipient-email" className="control-label">
                    Recipient: <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="recipient-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-default" onClick={() => setMail(false)}>
                  Close
                </button>
                <button type="button" className="btn btn-primary sendReport" onClick={() => void onSend()}>
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Un tichet = DOUA randuri. Primul poarta cele noua celule ale tichetului, cu
 * `rowSpan={2}`, si numele starilor; al doilea, cat s-a stat in fiecare.
 */
function TicketRows({ ticket, max }: { ticket: CompletionTicket; max: number }) {
  const slots = Array.from({ length: max }, (_, i) => ticket.completion_time[i] ?? null)

  return (
    <>
      <tr>
        <td rowSpan={2}>{ticket.ID}</td>
        <td rowSpan={2}>{ticket.company}</td>
        <td rowSpan={2}>{ticket.subject}</td>
        <td rowSpan={2}>{ticket.category_name}</td>
        <td rowSpan={2}>{ticket.subcategory_name}</td>
        <td rowSpan={2}>{ticket.operation_name}</td>
        <td rowSpan={2}>{ticket.estimate_effort ? durationString(ticket.estimate_effort) : ''}</td>
        <td rowSpan={2}>{ticket.real_effort ? durationString(ticket.real_effort) : ''}</td>
        <td rowSpan={2}>{ticket.active_time ? durationString(ticket.active_time) : ''}</td>
        {slots.map((step, i) => (
          <td key={i}>{step ? step.status_name : ''}</td>
        ))}
      </tr>
      <tr>
        {slots.map((step, i) => (
          <td key={i}>{step ? <StatusCompletion step={step} /> : null}</td>
        ))}
      </tr>
    </>
  )
}

/**
 * Cat s-a stat in stare fata de cat era socotit.
 *
 * `estimated_effort` e in MINUTE si se inmulteste cu 60 - ca in
 * `statusCompletionTime` din `public/js/easeedesk/my_performance.js`. Peste:
 * rosu si "EXCEEDED"; sub: verde si "EARLIER"; fix: casuta verde, goala.
 */
function StatusCompletion({ step }: { step: CompletionStep }) {
  if (!step.status_time) return <div className="color-green"></div>

  const estimated = step.estimated_effort * 60
  if (step.status_time > estimated) {
    return (
      <div className="color-red">
        {durationString(step.status_time - estimated)} <label className="label label-danger">EXCEEDED</label>
      </div>
    )
  }
  if (step.status_time < estimated) {
    return (
      <div className="color-green">
        {durationString(estimated - step.status_time)} <label className="label label-success">EARLIER</label>
      </div>
    )
  }

  return <div className="color-green"></div>
}
